import React, { useEffect, useState } from "react";
import { Person, EventDto, Zone } from "./types";
import QRGenerator from "../../../components/QRGenerator";
import ReactDOM from "react-dom";
import "./IDCardPrint.css";

interface TemplateField {
    id: number;
    fieldName: string;
    x: number;
    y: number;
    width: number;
    height: number;
    enabled: boolean;
    styles?: any;
}

interface Template {
    id: number;
    name: string;
    fields: TemplateField[];
    background?: string;
}

interface Props {
    persons: Person[];
    template: Template;
    selectedEvent: EventDto;
    functionsWithZones?: { functionName: string; assignedZones?: Zone[] }[];
    onClose: () => void;
    onPrinted?: (persons: Person[]) => void; // <-- use Person[]
}

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;
const CARD_HEIGHT = A4_HEIGHT / 2;
const webUrl = process.env.REACT_APP_WEB_URL!;

const makeImageUrl = (path?: string | null) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${webUrl}${path}`;
};


const MARGIN_TOP = 38;       // 1 cm top/bottom
const MARGIN_BOTTOM = 38;
const MARGIN_SIDE = 64;      // 1.7 cm left/right
const MIDDLE_GAP = 64;       // 1.7 cm gap between two cards

const PRINTABLE_WIDTH = A4_WIDTH - MARGIN_SIDE * 2;
const CARD_HEIGHT_WITH_MARGINS = (A4_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM - MIDDLE_GAP) / 2;

const mapStylesForPrint = (styles: any, isShape = false) => ({
    fontSize: styles?.FontSize ?? styles?.fontSize ?? 14,
    fontFamily: styles?.FontFamily ?? styles?.fontFamily ?? "Arial",
    fontWeight: styles?.FontWeight ?? styles?.fontWeight ?? "normal",
    fontStyle: styles?.FontStyle ?? styles?.fontStyle ?? "normal",
    color: styles?.Color ?? styles?.color ?? "#000",
    hasBorder: styles?.HasBorder ?? styles?.hasBorder ?? false,
    backgroundColor:
        styles?.BackgroundColor ??
        styles?.backgroundColor ??
        (isShape ? "#ccc" : "transparent"),
    borderColor: styles?.BorderColor ?? styles?.borderColor ?? "#000",
    borderWidth: styles?.BorderWidth ?? styles?.borderWidth ?? 2,
    borderRadius: styles?.BorderRadius ?? styles?.borderRadius ?? 0,
    textAlign: styles?.TextAlign ?? styles?.textAlign ?? "left",
    zIndex: isShape ? 1 : 2,
});

const IDCardPrint: React.FC<Props> = ({
    persons,
    template,
    selectedEvent,
    onClose,
    onPrinted,
}) => {
    const apiUrl = process.env.REACT_APP_API_URL!;
    const background = selectedEvent.eventIDCardPath;
    const selectedEventId = selectedEvent.eventID;
    const [eventZones, setEventZones] = useState<Zone[]>([]);
    const [showCopy, setShowCopy] = useState(false);
    const [showZones, setShowZones] = useState(false); // new checkbox
    const [zoneVisibility, setZoneVisibility] = useState<Record<number, boolean>>({});


    useEffect(() => {
        if (!selectedEventId) return;

        fetch(`${apiUrl}/api/zones/event/${selectedEventId}`)
            .then(res => res.json())
            .then(data => setEventZones(data))
            .catch(err =>
                console.error("Failed loading event zones", err)
            );

    }, [selectedEventId]);

    const makeImageUrl = (path?: string | null) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${webUrl}${path}`;
    };

    const getFunctionsForEvent = (person: Person) =>
        person.functions ?? [];

    const getZonesForEvent = (person: Person) => {
        const zones = person.zones ?? [];

        const uniqueMap = new Map<string, Zone>();

        zones.forEach((z) => {
            const key = `${z.number}-${z.zoneName}`;
            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, z);
            }
        });

        return Array.from(uniqueMap.values());
    };



    const enrichPersonsForPrint = async () => {
        const updatedPersons = await Promise.all(
            persons.map(async (p) => {
                try {
                    const [personRes, shiftsRes] = await Promise.all([
                        fetch(`${apiUrl}/api/persons/${p.personId}`),
                        fetch(`${apiUrl}/api/shifts/my?personId=${p.personId}&eventId=${selectedEventId}`),
                    ]);

                    let personImagePath = p.personImagePath ?? "";
                    let hasProfileImage = p.hasProfileImage ?? false;
                    let shifts: any[] = [];

                    if (personRes.ok) {
                        const personData = await personRes.json();
                        personImagePath = personData.personImagePath ?? "";
                        hasProfileImage = !!personData.personImagePath || personData.hasImage === true;
                    }

                    if (shiftsRes.ok) {
                        const shiftsData = await shiftsRes.json();
                        shifts = shiftsData.confirmed ?? [];
                    }

                    return {
                        ...p,
                        personImagePath,
                        hasProfileImage,
                        shifts,
                    };
                } catch (err) {
                    console.error(`Failed loading print data for person ${p.personId}`, err);
                    return {
                        ...p,
                        personImagePath: p.personImagePath ?? "",
                        hasProfileImage: p.hasProfileImage ?? false,
                        shifts: [],
                    };
                }
            })
        );

        return updatedPersons;
    };

    //const fetchShiftsForPersons = async () => {
    //    const updatedPersons = await Promise.all(
    //        persons.map(async (p) => {
    //            const res = await fetch(`${apiUrl}/api/shifts/my?personId=${p.personId}&eventId=${selectedEventId}`);
    //            const data = await res.json();

    //            return {
    //                ...p,
    //                shifts: data.confirmed ?? []
    //            };
    //        })
    //    );

    //    return updatedPersons;
    //};

    const openPrintWindow = async () => {
        if (!selectedEventId)
            return alert("Select an event first");

        const personsWithShifts = await enrichPersonsForPrint();

        const zoneVisibilityCopy = { ...zoneVisibility };
        const showZonesCopy = showZones;

        const win = window.open("", "_blank");
        if (!win) return;

        win.document.write(`
        <html>
        <head>
            <title>ID Cards</title>
            <style>
                @media print {
                    @page { size: A4 portrait; margin: 0; }
                    body { margin: 0; padding: 0; }
                }
                body { font-family: Arial, sans-serif; margin:0; padding:0;}
                .page {
                        width: ${A4_WIDTH}px;
                        height: ${A4_HEIGHT}px;
                        position: relative;
                        page-break-after: always;
                    }
                .card {
                        width: ${PRINTABLE_WIDTH}px; 
                        height: ${CARD_HEIGHT_WITH_MARGINS}px; /* use height with margins */
                        position: absolute; 
                        background-size: cover; 
                        background-repeat: no-repeat; 
                        overflow: hidden; 
                    }
                .field { position:absolute; display:flex; align-items:center; justify-content:center; box-sizing:border-box; }
                .field img { width:100%; height:100%; object-fit:cover; border-radius:6px; }
            </style>
        </head>
        <body>
        `);

        personsWithShifts.forEach((person, index) => {
            if (index % 2 === 0) win.document.write(`<div class="page">`);

            const isTop = index % 2 === 0;
            const topOffset = isTop
                ? MARGIN_TOP
                : MARGIN_TOP + CARD_HEIGHT_WITH_MARGINS + MIDDLE_GAP;

            const scaleX = PRINTABLE_WIDTH / A4_WIDTH;
            const scaleY = CARD_HEIGHT_WITH_MARGINS / (A4_HEIGHT / 2);

            win.document.write(
                `<div class="card" style="
                  top:${topOffset}px; 
                  left:${MARGIN_SIDE}px; 
                  width:${PRINTABLE_WIDTH}px; 
                  height:${CARD_HEIGHT_WITH_MARGINS}px; 
                  background-image:url('${background ?? ""}');
              ">`
                        );

            template.fields
                .filter((f) => {
                    if (!f.enabled) return false;

                    // ❌ Hide "copy" unless checkbox is checked
                    if (f.fieldName === "copy" && !showCopy) return false;

                    // ❌ Hide "shifts" if 3 or more shifts
                    if (f.fieldName === "shifts") {
                        const shifts = (person as any).shifts ?? [];
                        if (shifts.length >= 3) return false;
                    }

                    return true;
                })
                .forEach((f) => {
                    const isShape =
                        f.fieldName === "shape-rect" || f.fieldName === "shape-circle";

                    const finalStyles = mapStylesForPrint(f.styles, isShape);

                    let content = "";

                    switch (f.fieldName) {
                        case "profile": {
                            const imageSrc =
                                typeof person.personImagePath === "string" && person.personImagePath.trim() !== ""
                                    ? makeImageUrl(person.personImagePath)
                                    : "";

                            content = imageSrc
                                ? `<img src="${imageSrc}" alt="Profile" style="width:100%;height:100%;object-fit:cover;border-radius:6px;" />`
                                : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#ccc;border-radius:6px;">No Image</div>`;
                            break;
                        }

                        case "name":
                            content = `<span style="
                        font-size:${finalStyles.fontSize * scaleY}px;
                        font-family:${finalStyles.fontFamily};
                        font-weight:${finalStyles.fontWeight};
                        font-style:${finalStyles.fontStyle};
                        color:${finalStyles.color};
                        background-color:${finalStyles.backgroundColor};
                        width:100%;
                        height:100%;
                        display:flex;
                        align-items:center;
                        justify-content:${finalStyles.textAlign === "left"
                                    ? "flex-start"
                                    : finalStyles.textAlign === "right"
                                        ? "flex-end"
                                        : "center"};
                        text-align:${finalStyles.textAlign};
                        padding:4px;
                    ">
                        ${person.firstName} ${person.lastName}
                    </span>`;
                            break;

                        case "function":
                            content = `<span style="
                                font-size:${finalStyles.fontSize}px;
                                font-family:${finalStyles.fontFamily};
                                font-weight:${finalStyles.fontWeight};
                                font-style:${finalStyles.fontStyle};
                                text-align:${finalStyles.textAlign};
                                color:${finalStyles.color};
                                background-color:${finalStyles.backgroundColor};
                                width:100%;
                                height:100%;
                                display:flex;
                                align-items:center;
                                justify-content:${finalStyles.textAlign === "left"
                                    ? "flex-start"
                                    : finalStyles.textAlign === "right"
                                        ? "flex-end"
                                        : "center"
                                };
                                text-align:${finalStyles.textAlign};
                                padding:4px;
                            ">${getFunctionsForEvent(person).join(
                                ", "
                            )}</span>`;
                            break;

                        case "title":
                            content = `<span style="
                                font-size:${finalStyles.fontSize}px;
                                font-family:${finalStyles.fontFamily};
                                font-weight:${finalStyles.fontWeight};
                                font-style:${finalStyles.fontStyle};
                                text-align:${finalStyles.textAlign};
                                color:${finalStyles.color};
                                background-color:${finalStyles.backgroundColor};
                                width:100%;
                                height:100%;
                                display:flex;
                                align-items:center;
                                justify-content:${finalStyles.textAlign === "left"
                                                            ? "flex-start"
                                                            : finalStyles.textAlign === "right"
                                                                ? "flex-end"
                                                                : "center"
                                                        };
                                padding:4px;
                            ">
                            ${person.title ?? ""}
                        </span>`;
                            break;
                        case "company":
                            content = `<span style="
                                font-size:${finalStyles.fontSize}px;
                                font-family:${finalStyles.fontFamily};
                                font-weight:${finalStyles.fontWeight};
                                font-style:${finalStyles.fontStyle};
                                text-align:${finalStyles.textAlign};
                                color:${finalStyles.color};
                                background-color:${finalStyles.backgroundColor};
                                width:100%;
                                height:100%;
                                display:flex;
                                align-items:center;
                                justify-content:${finalStyles.textAlign === "left"
                                                            ? "flex-start"
                                                            : finalStyles.textAlign === "right"
                                                                ? "flex-end"
                                                                : "center"
                                                        };
                                padding:4px;
                            ">
                                ${person.company ?? ""}
                            </span>`;
                            break;
                        case "zonesList":
                            const zones = eventZones;

                            content = zones.length
                                ? `<ul style="
                                margin:0;
                                padding:0;
                                list-style:none;
                                font-size:${finalStyles.fontSize}px;
                                font-family:${finalStyles.fontFamily};
                                font-weight:${finalStyles.fontWeight};
                                font-style:${finalStyles.fontStyle};
                                color:${finalStyles.color};
                                text-align:${finalStyles.textAlign};
                            ">
                                ${zones
                                                        .map(
                                                            (z) =>
                                                                `<li style="
                                                display:block;
                                                text-align:${finalStyles.textAlign};
                                            ">
                                            ${z.number} - ${z.zoneName}
                                            </li>`
                                                        )
                                                        .join("")}
                            </ul>`
                                : "";
                            break;

                        case "zoneCircles":
                            let zonesToShow;

                            if (!showZonesCopy) {
                                // ✅ Default: show person's assigned zones
                                zonesToShow = getZonesForEvent(person);
                            } else {
                                // ✅ Checkbox ON: use manually selected zones
                                zonesToShow = eventZones.filter(
                                    (z) => zoneVisibilityCopy[z.zoneID]
                                );
                            }

                            content = zonesToShow
                                .map(
                                    (z) => `
                                    <div style="
                                      width:30px;
                                      height:30px;
                                      border-radius:50%;
                                      border:${finalStyles.borderWidth}px solid ${finalStyles.borderColor};
                                      display:flex;
                                      align-items:center;
                                      justify-content:center;
                                      font-size:${finalStyles.fontSize}px;
                                      font-family:${finalStyles.fontFamily};
                                      font-weight:${finalStyles.fontWeight};
                                      font-style:${finalStyles.fontStyle};
                                      color:${finalStyles.color};
                                      background-color:${finalStyles.backgroundColor};
                                    ">${z.number}</div>
                                  `
                                )
                                .join("");

                            content = `<div style="display:flex;flex-wrap:wrap;gap:4px;">${content}</div>`;
                            break;
                        case "qr":
                            content = `<div id="qr-${person.personId}-${index}"></div>`;
                            break;
                        case "copy":
                            content = `<span style="
                                font-size:${finalStyles.fontSize * scaleY}px;
                                font-family:${finalStyles.fontFamily};
                                color:${finalStyles.color};
                            ">
                                Kopi
                            </span>`;
                            break;
                        case "shifts":
                            const shifts = (person as any).shifts ?? [];

                            // If 3 or more shifts, do not display anything
                            if (shifts.length >= 2) {
                                content = "";
                                break;
                            }

                            const getWeekday = (dateStr: string) =>
                                new Date(dateStr).toLocaleDateString("da-DK", { weekday: "long" });

                            // Get unique days
                            const uniqueDays = Array.from(
                                new Set(shifts.map((s: any) => getWeekday(s.start)))
                            ) as string[];

                            // Sort weekdays in Danish week order
                            const weekdayOrder = [
                                "mandag",
                                "tirsdag",
                                "onsdag",
                                "torsdag",
                                "fredag",
                                "lørdag",
                                "søndag",
                            ];

                            const sortedDays = uniqueDays.sort(
                                (a, b) => weekdayOrder.indexOf(a) - weekdayOrder.indexOf(b)
                            );

                            content = sortedDays.length
                                ? `
                                <div style="font-size:${finalStyles.fontSize * scaleY}px;">
                                    <div style="margin-bottom:4px;">
                                        Gyldigt på følgende dage:
                                    </div>
                                    ${sortedDays.map(day => `<div>${day}</div>`).join("")}
                                </div>
                                `
                                : "";
                            break;
                        case "shape-rect":
                        case "shape-circle":
                            content = `<div style="
                                width:100%;
                                height:100%;
                                background-color:${finalStyles.backgroundColor ?? "#ccc"};
                                border:${finalStyles.hasBorder
                                    ? `${finalStyles.borderWidth ?? 1}px solid ${finalStyles.borderColor ?? "#000"}`
                                    : "none"};
                                border-radius:${f.fieldName ===
                                    "shape-circle"
                                    ? "50%"
                                    : finalStyles.borderRadius ??
                                    0
                                }px;
                            "></div>`;
                            break;

                        default:
                            content =
                                (person as any)[
                                f.fieldName
                                ] ?? "";
                    }

                    win.document.write(`
                <div class="field" style="
                    left:${f.x * scaleX}px;
                    top:${f.y * scaleY}px;
                    width:${f.width * scaleX}px;
                    height:${f.height * scaleY}px;
                    position:absolute;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    box-sizing:border-box;
                    z-index:${finalStyles.zIndex};
                    font-size:${finalStyles.fontSize * scaleY}px;
                    font-family:${finalStyles.fontFamily};
                    font-weight:${finalStyles.fontWeight};
                    font-style:${finalStyles.fontStyle};
                    color:${finalStyles.color};
                    background-color:${finalStyles.backgroundColor};
                    text-align:center;
                ">
                    ${content}
                </div>
            `);
                });

            win.document.write(`</div>`);

            if (index % 2 === 1 || index === persons.length - 1)
                win.document.write(`</div>`);
        });

        win.document.write(
            `<script>
        window.onload = () => window.print();
        window.onafterprint = () => console.log("Print dialog closed");
    </script></body></html>`
        );

        if (onPrinted) onPrinted(persons);

        win.document.close();

        // Render QR codes
        persons.forEach((person, index) => {
            template.fields
                .filter(
                    (f) =>
                        f.enabled &&
                        f.fieldName === "qr"
                )
                .forEach((f) => {
                    const el =
                        win.document.getElementById(
                            `qr-${person.personId}-${index}`
                        );

                    if (el) {
                        ReactDOM.render(
                            <QRGenerator
                                value={`${webUrl}/epos/${person.personId}/${selectedEventId}`}
                                size={f.width}
                            />,
                            el
                        );
                    }
                });
        });
    };

    return (
        <div
            className="idprint-overlay"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
                overflowY: "auto",
                padding: 20,
            }}
        >
            <div
                className="idprint-panel"
                style={{
                    backgroundColor: "#fff",
                    borderRadius: 12,
                    padding: 24,
                    width: 400,
                    maxWidth: "100%",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                }}
            >
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
                    ID Card Print
                </h3>
                <p style={{ margin: 0, color: "#555" }}>
                    {persons.length} person(s) selected
                </p>

                {/* Checkboxes */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        marginBottom: 12,
                    }}
                >
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                            type="checkbox"
                            checked={showCopy}
                            onChange={(e) => setShowCopy(e.target.checked)}
                        />
                        Vis "Kopi" på ID kort
                    </label>

                    {persons.length === 1 && (
                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={showZones}
                                onChange={(e) => {
                                    setShowZones(e.target.checked);

                                    if (e.target.checked && Object.keys(zoneVisibility).length === 0) {
                                        const initialVisibility: Record<number, boolean> = {};
                                        eventZones.forEach((z) => {
                                            // green for zones the person already has, grey otherwise
                                            const isAssigned = getZonesForEvent(persons[0]).some(
                                                (pz) => pz.zoneID === z.zoneID
                                            );
                                            initialVisibility[z.zoneID] = isAssigned;
                                        });
                                        setZoneVisibility(initialVisibility);
                                    }
                                }}
                            />
                            Vis zoner
                        </label>
                    )}
                </div>

                {showZones && (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            maxHeight: 150,
                            overflowY: "auto",
                            padding: 4,
                            border: "1px solid #ddd",
                            borderRadius: 6,
                            backgroundColor: "#f9f9f9",
                        }}
                    >
                        {eventZones.map((zone) => {
                            const isVisible = zoneVisibility[zone.zoneID] ?? false;

                            return (
                                <button
                                    key={zone.zoneID}
                                    onClick={() =>
                                        setZoneVisibility((prev) => ({
                                            ...prev,
                                            [zone.zoneID]: !prev[zone.zoneID],
                                        }))
                                    }
                                    style={{
                                        padding: "6px 10px",
                                        borderRadius: 4,
                                        border: "1px solid #000",
                                        fontSize: 12,
                                        backgroundColor: isVisible ? "#4caf50" : "#e0e0e0",
                                        cursor: "pointer",
                                        flex: "0 0 auto",
                                    }}
                                >
                                    {zone.number} - {zone.zoneName}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Action buttons */}
                <div
                    style={{
                        display: "flex",
                        gap: 12,
                        justifyContent: "flex-end",
                        marginTop: 8,
                    }}
                >
                    <button
                        className="btn btn-secondary"
                        onClick={onClose}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 6,
                            border: "1px solid #ccc",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        Close
                    </button>

                    <button
                        className="btn btn-primary"
                        onClick={openPrintWindow}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 6,
                            border: "none",
                            backgroundColor: "#007bff",
                            color: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        Print
                    </button>
                </div>
            </div>
        </div>
    );
};

export default IDCardPrint;